import { useCallback, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { UserMeContext } from '../contexts/UserMeContext'
import useAuth from './useAuth'
import {
    consumeSkipNextUserSettingsSync,
    getStoredLanguage,
    markUserLanguageChoice,
    resolveLanguage,
    setStoredLanguage,
    SupportedLanguage,
} from '../i18n/languagePreference'
import type { UserMe } from '../api/UserApi'

function getSettingsLanguage(user: UserMe | null): SupportedLanguage | null {
    const language = user?.settings?.ui?.language
    return language === 'sv' || language === 'en' ? language : null
}

export function useLanguagePreferenceSync() {
    const { i18n } = useTranslation()
    const { isAuthenticated } = useAuth()
    const { user } = useContext(UserMeContext)

    useEffect(() => {
        if (!isAuthenticated || !user) {
            return
        }

        if (consumeSkipNextUserSettingsSync()) {
            return
        }

        const settingsLanguage = getSettingsLanguage(user)
        if (!settingsLanguage) {
            return
        }

        const storedLanguage = getStoredLanguage()
        if (storedLanguage !== settingsLanguage) {
            setStoredLanguage(settingsLanguage)
        }

        const activeLanguage = resolveLanguage(i18n.resolvedLanguage ?? i18n.language)
        if (activeLanguage !== settingsLanguage) {
            void i18n.changeLanguage(settingsLanguage)
        }
    }, [isAuthenticated, user, user?.settings?.ui?.language, i18n])
}

export function useLanguagePreference() {
    const { i18n } = useTranslation()
    const { isAuthenticated } = useAuth()
    const { updateMe } = useContext(UserMeContext)

    const setLanguage = useCallback(
        async (language: SupportedLanguage) => {
            markUserLanguageChoice()

            await i18n.changeLanguage(language)
            setStoredLanguage(language)

            if (isAuthenticated) {
                await updateMe({ settings: { ui: { language } } })
            }
        },
        [i18n, isAuthenticated, updateMe],
    )

    const language = resolveLanguage(i18n.resolvedLanguage ?? i18n.language)

    return { language, setLanguage }
}
